import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, AreaChart, Area,
  BarChart, Bar, PieChart, Pie, Cell,
} from "recharts";
import {
  LogOut, Eye, Users, Inbox, TrendingUp, TrendingDown, Monitor, Smartphone, Tablet,
  Globe, Search, Share2, Link as LinkIcon, ChevronDown, Mail, Clock, ArrowUpRight, Download,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RANGES = [
  { label: "7D", value: 7 },
  { label: "30D", value: 30 },
  { label: "90D", value: 90 },
];

const CMYK = ["#00AEEF", "#EC008C", "#FFF200", "#0E0E0E"];

function pct(current, previous) {
  if (!previous) return current > 0 ? 100 : 0;
  return Math.round(((current - previous) / previous) * 100);
}

function referrerIcon(src) {
  const s = (src || "").toLowerCase();
  if (s === "direct") return <LinkIcon size={14} />;
  if (s === "search") return <Search size={14} />;
  if (s === "social") return <Share2 size={14} />;
  return <Globe size={14} />;
}

function deviceIcon(d) {
  if (d === "mobile") return <Smartphone size={14} />;
  if (d === "tablet") return <Tablet size={14} />;
  return <Monitor size={14} />;
}

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("en-AU", { month: "short", day: "numeric" });
}

function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [days, setDays] = useState(30);
  const [tab, setTab] = useState("overview");

  const [summary, setSummary] = useState(null);
  const [series, setSeries] = useState([]);
  const [referrers, setReferrers] = useState([]);
  const [pages, setPages] = useState([]);
  const [devices, setDevices] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [replayUrl, setReplayUrl] = useState("");
  const [replayNote, setReplayNote] = useState("");
  const [replaySending, setReplaySending] = useState(false);
  const [waitlist, setWaitlist] = useState([]);
  const [reminderUrl, setReminderUrl] = useState("");
  const [reminderNote, setReminderNote] = useState("");
  const [reminderSending, setReminderSending] = useState(false);
  const [wsSettings, setWsSettings] = useState(null);
  const [wsDate, setWsDate] = useState("");
  const [wsTime, setWsTime] = useState("");
  const [wsSaving, setWsSaving] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);

  useEffect(() => {
    const loadAll = async () => {
      try {
        const [s, ts, ref, pg, dev, enq, wr, wl, wset] = await Promise.all([
          axios.get(`${API}/analytics/summary?days=${days}`, { withCredentials: true }),
          axios.get(`${API}/analytics/timeseries?days=${days}`, { withCredentials: true }),
          axios.get(`${API}/analytics/referrers?days=${days}`, { withCredentials: true }),
          axios.get(`${API}/analytics/pages?days=${days}`, { withCredentials: true }),
          axios.get(`${API}/analytics/devices?days=${days}`, { withCredentials: true }),
          axios.get(`${API}/contact`, { withCredentials: true }),
          axios.get(`${API}/workshop/registrations`, { withCredentials: true }),
          axios.get(`${API}/workshop/waitlist`, { withCredentials: true }),
          axios.get(`${API}/workshop/settings`, { withCredentials: true }),
        ]);
        setSummary(s.data);
        setSeries(ts.data.series);
        setReferrers(ref.data.referrers);
        setPages(pg.data.pages);
        setDevices(dev.data.devices);
        setEnquiries(enq.data);
        setRegistrations(wr.data.registrations);
        setWaitlist(wl.data.waitlist);
        setWsSettings(wset.data.settings);
        const iso = wset.data.settings.workshop_date_iso || "";
        setWsDate((prev) => prev || iso.slice(0, 10));
        setWsTime((prev) => prev || (iso.includes("T") ? iso.slice(11, 16) : ""));
      } catch (err) {
        toast.error("Failed to load dashboard data");
        console.error(err);
      }
    };
    loadAll();
  }, [days]);

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const updateEnquiryStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/contact/${id}/status?status=${status}`, {}, { withCredentials: true });
      setEnquiries((prev) => prev.map((e) => (e.id === id ? { ...e, status } : e)));
      if (selectedEnquiry?.id === id) setSelectedEnquiry({ ...selectedEnquiry, status });
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error("Failed to update status");
    }
  };

  const saveWorkshopDate = async (newRound) => {
    if (!wsDate || !wsTime) {
      toast.error("Pick a date and time first");
      return;
    }
    if (newRound && !window.confirm(
      `Start a new round on ${wsDate} at ${wsTime}? Current registrations and waitlist are archived, and 8 fresh seats open on the new date.`
    )) return;
    setWsSaving(true);
    try {
      const payload = { workshop_date_iso: `${wsDate}T${wsTime}:00` };
      const { data } = newRound
        ? await axios.post(`${API}/workshop/new-round`, payload, { withCredentials: true })
        : await axios.put(`${API}/workshop/settings`, payload, { withCredentials: true });
      setWsSettings(data.settings);
      toast.success(newRound ? `Round ${data.settings.round} is live — 8 fresh seats open` : "Workshop date updated");
      const [wr, wl] = await Promise.all([
        axios.get(`${API}/workshop/registrations`, { withCredentials: true }),
        axios.get(`${API}/workshop/waitlist`, { withCredentials: true }),
      ]);
      setRegistrations(wr.data.registrations);
      setWaitlist(wl.data.waitlist);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save workshop date");
    } finally {
      setWsSaving(false);
    }
  };

  const sendReminder = async () => {
    if (!window.confirm(`Email a day-of reminder to ${registrations.length} registered attendee${registrations.length === 1 ? "" : "s"}?`)) return;
    setReminderSending(true);
    try {
      const { data } = await axios.post(
        `${API}/workshop/reminder`,
        { join_url: reminderUrl.trim() || undefined, note: reminderNote.trim() || undefined },
        { withCredentials: true }
      );
      if (data.failed === 0) toast.success(`Reminder emailed to all ${data.sent} attendee${data.sent === 1 ? "" : "s"}`);
      else toast.warning(`Sent to ${data.sent}, failed for ${data.failed} — check backend logs`);
      setReminderUrl("");
      setReminderNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send reminders");
    } finally {
      setReminderSending(false);
    }
  };

  const sendReplay = async () => {
    if (!replayUrl.trim()) {
      toast.error("Paste the recording link first");
      return;
    }
    if (!window.confirm(`Email the replay to ${registrations.length} registered attendee${registrations.length === 1 ? "" : "s"}?`)) return;
    setReplaySending(true);
    try {
      const { data } = await axios.post(
        `${API}/workshop/replay`,
        { recording_url: replayUrl.trim(), note: replayNote.trim() || undefined },
        { withCredentials: true }
      );
      if (data.failed === 0) toast.success(`Replay emailed to all ${data.sent} attendee${data.sent === 1 ? "" : "s"}`);
      else toast.warning(`Sent to ${data.sent}, failed for ${data.failed} — check backend logs`);
      setReplayUrl("");
      setReplayNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send replay emails");
    } finally {
      setReplaySending(false);
    }
  };

  if (!summary) {
    return (
      <div className="min-h-screen bg-[#0E0E0E] text-white flex items-center justify-center">
        <div className="font-mono text-sm animate-pulse">Loading dashboard...</div>
      </div>
    );
  }

  const viewsChange = pct(summary.total_views, summary.prev_views);
  const uniqueChange = pct(summary.unique_visitors, summary.prev_unique_visitors);

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0E0E0E] text-white sticky top-0 z-50 border-b-2 border-black">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="/logos/gm-logo-dark.png" alt="GM Print" width={36} height={36} />
            <div>
              <div className="font-display text-lg leading-none">GM PRINT</div>
              <div className="font-mono text-[9px] tracking-widest text-white/50 mt-0.5">ADMIN DASHBOARD</div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden sm:block text-right">
              <div className="font-mono text-[10px] text-white/50 uppercase tracking-widest">Signed in</div>
              <div className="text-sm">{user?.name}</div>
            </div>
            <button
              onClick={handleLogout}
              data-testid="admin-logout-btn"
              className="flex items-center gap-2 border border-white/20 px-4 py-2 hover:bg-white hover:text-black transition-colors text-sm font-body"
            >
              <LogOut size={14} /> Sign out
            </button>
          </div>
        </div>
        <div className="cmyk-bar cmyk-bar-thin">
          <div className="c" /><div className="m" /><div className="y" /><div style={{background:'#fff'}} />
        </div>
      </header>

      {/* Tab + Range */}
      <div className="max-w-7xl mx-auto px-6 pt-8 flex flex-wrap items-center justify-between gap-4">
        <div className="flex gap-1 border-2 border-black bg-white" data-testid="admin-tabs">
          <button
            onClick={() => setTab("overview")}
            data-testid="admin-tab-overview"
            className={`px-5 py-2 font-body text-sm uppercase tracking-widest font-semibold ${tab === "overview" ? "bg-black text-white" : "hover:bg-black/5"}`}
          >Overview</button>
          <button
            onClick={() => setTab("enquiries")}
            data-testid="admin-tab-enquiries"
            className={`px-5 py-2 font-body text-sm uppercase tracking-widest font-semibold ${tab === "enquiries" ? "bg-black text-white" : "hover:bg-black/5"}`}
          >Enquiries {enquiries.filter(e => e.status === "new").length > 0 && (<span className="ml-1 bg-[#EC008C] text-white px-2 py-0.5 text-[10px]">{enquiries.filter(e => e.status === "new").length}</span>)}</button>
          <button
            onClick={() => setTab("workshop")}
            data-testid="admin-tab-workshop"
            className={`px-5 py-2 font-body text-sm uppercase tracking-widest font-semibold ${tab === "workshop" ? "bg-black text-white" : "hover:bg-black/5"}`}
          >Workshop {registrations.length > 0 && (<span className="ml-1 bg-[#00AEEF] text-white px-2 py-0.5 text-[10px]">{registrations.length}</span>)}</button>
        </div>

        {tab === "overview" && (
          <div className="flex gap-1 border-2 border-black bg-white" data-testid="admin-range-selector">
            {RANGES.map((r) => (
              <button
                key={r.value}
                onClick={() => setDays(r.value)}
                data-testid={`admin-range-${r.value}`}
                className={`px-4 py-2 font-mono text-xs ${days === r.value ? "bg-black text-white" : "hover:bg-black/5"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        )}
      </div>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {tab === "overview" && (
          <>
            {/* Stat Cards */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
            >
              <StatCard
                label="Total Views"
                value={summary.total_views}
                change={viewsChange}
                icon={<Eye size={20} />}
                accent="#00AEEF"
                testid="stat-total-views"
              />
              <StatCard
                label="Unique Visitors"
                value={summary.unique_visitors}
                change={uniqueChange}
                icon={<Users size={20} />}
                accent="#EC008C"
                testid="stat-unique-visitors"
              />
              <StatCard
                label="Enquiries"
                value={summary.enquiries}
                icon={<Inbox size={20} />}
                accent="#FFF200"
                testid="stat-enquiries"
                accentDark
              />
              <StatCard
                label="Conversion"
                value={summary.unique_visitors > 0 ? `${((summary.enquiries / summary.unique_visitors) * 100).toFixed(1)}%` : "—"}
                icon={<TrendingUp size={20} />}
                accent="#0E0E0E"
                testid="stat-conversion"
                accentDark
              />
            </motion.div>

            {/* Time-series chart */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              className="bg-white border-2 border-black p-6 mb-8"
              data-testid="admin-timeseries-chart"
            >
              <div className="flex items-center justify-between mb-6">
                <div>
                  <div className="label-caps text-black/50">Daily Traffic</div>
                  <h3 className="font-display text-2xl">Visits over time</h3>
                </div>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={series} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="viewsGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00AEEF" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#00AEEF" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="uniqueGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#EC008C" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#EC008C" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                  <XAxis dataKey="date" tickFormatter={formatDate} stroke="#666" style={{ fontSize: 11, fontFamily: "JetBrains Mono" }} />
                  <YAxis stroke="#666" style={{ fontSize: 11, fontFamily: "JetBrains Mono" }} allowDecimals={false} />
                  <Tooltip
                    labelFormatter={formatDate}
                    contentStyle={{ background: "#0E0E0E", border: "none", color: "#fff", fontFamily: "Inter", fontSize: 12 }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Area type="monotone" dataKey="views" stroke="#00AEEF" strokeWidth={2} fill="url(#viewsGrad)" name="Views" />
                  <Area type="monotone" dataKey="unique_visitors" stroke="#EC008C" strokeWidth={2} fill="url(#uniqueGrad)" name="Unique visitors" />
                </AreaChart>
              </ResponsiveContainer>
              <div className="flex gap-6 mt-4 font-mono text-xs text-black/60">
                <div className="flex items-center gap-2"><span className="w-3 h-3 bg-[#00AEEF]" /> Views</div>
                <div className="flex items-center gap-2"><span className="w-3 h-3 bg-[#EC008C]" /> Unique visitors</div>
              </div>
            </motion.div>

            {/* Referrers + Devices Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2 }}
                className="bg-white border-2 border-black p-6"
                data-testid="admin-referrers-panel"
              >
                <div className="label-caps text-black/50 mb-1">Top Sources</div>
                <h3 className="font-display text-2xl mb-6">Where visitors come from</h3>
                <div className="space-y-3">
                  {referrers.length === 0 && <div className="text-sm text-black/40 font-mono">No data yet</div>}
                  {referrers.map((r, i) => {
                    const total = referrers.reduce((sum, x) => sum + x.count, 0);
                    const pct = total > 0 ? Math.round((r.count / total) * 100) : 0;
                    return (
                      <div key={r.source} data-testid={`referrer-${i}`}>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="flex items-center gap-2 font-body">
                            {referrerIcon(r.source)}
                            <span className="capitalize">{r.source}</span>
                          </span>
                          <span className="font-mono text-xs">{r.count} <span className="text-black/40">· {pct}%</span></span>
                        </div>
                        <div className="w-full h-1.5 bg-black/5">
                          <div className="h-full bg-black" style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.25 }}
                className="bg-white border-2 border-black p-6"
                data-testid="admin-devices-panel"
              >
                <div className="label-caps text-black/50 mb-1">Device Breakdown</div>
                <h3 className="font-display text-2xl mb-6">How they visit</h3>
                <div className="flex items-center gap-6">
                  <ResponsiveContainer width="50%" height={160}>
                    <PieChart>
                      <Pie
                        data={devices.map((d) => ({ name: d.device, value: d.count }))}
                        dataKey="value"
                        cx="50%"
                        cy="50%"
                        innerRadius={40}
                        outerRadius={70}
                        stroke="#000"
                        strokeWidth={2}
                      >
                        {devices.map((_, i) => (
                          <Cell key={i} fill={CMYK[i % CMYK.length]} />
                        ))}
                      </Pie>
                      <Tooltip contentStyle={{ background: "#0E0E0E", border: "none", color: "#fff", fontSize: 12 }} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="flex-1 space-y-2">
                    {devices.length === 0 && <div className="text-sm text-black/40 font-mono">No data</div>}
                    {devices.map((d, i) => (
                      <div key={d.device} className="flex items-center justify-between text-sm" data-testid={`device-${d.device}`}>
                        <span className="flex items-center gap-2 capitalize font-body">
                          <span className="w-3 h-3" style={{ background: CMYK[i % CMYK.length] }} />
                          {deviceIcon(d.device)} {d.device}
                        </span>
                        <span className="font-mono text-xs">{d.count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Top pages */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="bg-white border-2 border-black p-6"
              data-testid="admin-pages-panel"
            >
              <div className="label-caps text-black/50 mb-1">Top Pages</div>
              <h3 className="font-display text-2xl mb-6">Most-viewed URLs</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-black text-left">
                      <th className="py-3 label-caps text-black/60">Path</th>
                      <th className="py-3 label-caps text-black/60 text-right">Views</th>
                      <th className="py-3 label-caps text-black/60 text-right">Unique</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pages.length === 0 && (
                      <tr><td colSpan={3} className="py-8 text-center text-black/40 font-mono">No data yet</td></tr>
                    )}
                    {pages.map((p, i) => (
                      <tr key={p.path} className="border-b border-black/10" data-testid={`page-row-${i}`}>
                        <td className="py-3 font-mono text-xs">{p.path}</td>
                        <td className="py-3 text-right font-body font-semibold">{p.views}</td>
                        <td className="py-3 text-right font-mono text-black/60">{p.unique_visitors}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </>
        )}

        {tab === "enquiries" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="grid grid-cols-1 lg:grid-cols-5 gap-4"
            data-testid="admin-enquiries-panel"
          >
            {/* List */}
            <div className="lg:col-span-2 bg-white border-2 border-black">
              <div className="border-b-2 border-black p-4 flex items-center justify-between">
                <div>
                  <div className="label-caps text-black/50">Inbox</div>
                  <div className="font-display text-xl">{enquiries.length} total</div>
                </div>
              </div>
              <div className="max-h-[700px] overflow-y-auto">
                {enquiries.length === 0 && (
                  <div className="p-8 text-center text-black/40 font-mono text-sm">No enquiries yet</div>
                )}
                {enquiries.map((e) => (
                  <button
                    key={e.id}
                    onClick={() => setSelectedEnquiry(e)}
                    data-testid={`enquiry-item-${e.id}`}
                    className={`w-full text-left px-5 py-4 border-b border-black/10 hover:bg-black/5 transition-colors ${selectedEnquiry?.id === e.id ? "bg-black/5" : ""}`}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-body font-semibold text-sm truncate">{e.name}</div>
                      {e.status === "new" && <span className="bg-[#EC008C] text-white text-[9px] px-2 py-0.5 font-mono uppercase">New</span>}
                      {e.status === "replied" && <span className="bg-black text-white text-[9px] px-2 py-0.5 font-mono uppercase">Replied</span>}
                      {e.status === "archived" && <span className="bg-black/20 text-black text-[9px] px-2 py-0.5 font-mono uppercase">Archived</span>}
                    </div>
                    <div className="text-xs text-black/50 font-mono mb-1">{e.email}</div>
                    <div className="text-xs text-black/60 line-clamp-2">{e.message}</div>
                    <div className="flex items-center gap-1 text-[10px] text-black/40 font-mono mt-2">
                      <Clock size={10} /> {formatDateTime(e.created_at)}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Detail */}
            <div className="lg:col-span-3 bg-white border-2 border-black" data-testid="admin-enquiry-detail">
              {!selectedEnquiry ? (
                <div className="p-16 text-center text-black/40 font-mono text-sm">
                  Select an enquiry to view details
                </div>
              ) : (
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <div>
                      <div className="label-caps text-black/50">From</div>
                      <h3 className="font-display text-3xl mt-1">{selectedEnquiry.name}</h3>
                      <a href={`mailto:${selectedEnquiry.email}`} className="text-sm font-mono text-black/60 hover:text-black flex items-center gap-1 mt-1">
                        <Mail size={12} /> {selectedEnquiry.email}
                      </a>
                    </div>
                    <div className="text-right">
                      <div className="text-xs font-mono text-black/40">{formatDateTime(selectedEnquiry.created_at)}</div>
                    </div>
                  </div>

                  <div className="border-t-2 border-black py-6">
                    <div className="label-caps text-black/50 mb-3">Message</div>
                    <p className="font-body text-base leading-relaxed whitespace-pre-wrap">{selectedEnquiry.message}</p>
                  </div>

                  {selectedEnquiry.files?.length > 0 && (
                    <div className="border-t-2 border-black py-6" data-testid="enquiry-files">
                      <div className="label-caps text-black/50 mb-3">Attached artwork</div>
                      <ul className="space-y-2">
                        {selectedEnquiry.files.map((f) => (
                          <li key={f.id}>
                            <a
                              href={`${API}/contact/${selectedEnquiry.id}/files/${f.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-sm font-mono hover:text-[#EC008C] transition-colors"
                              data-testid={`enquiry-file-${f.id}`}
                            >
                              <Download size={14} /> {f.filename}
                              <span className="text-black/40">({Math.max(1, Math.round(f.size / 1024))} KB)</span>
                            </a>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="border-t-2 border-black pt-6 flex flex-wrap gap-3">
                    <a
                      href={`mailto:${selectedEnquiry.email}?subject=Re: Your enquiry - Gain Momentum Print`}
                      onClick={() => updateEnquiryStatus(selectedEnquiry.id, "replied")}
                      data-testid="enquiry-reply-btn"
                      className="flex items-center gap-2 bg-black text-white px-5 py-2.5 font-body text-sm uppercase tracking-widest font-semibold hover:bg-[#EC008C] transition-colors"
                    >
                      Reply <ArrowUpRight size={14} />
                    </a>
                    {selectedEnquiry.status !== "archived" && (
                      <button
                        onClick={() => updateEnquiryStatus(selectedEnquiry.id, "archived")}
                        data-testid="enquiry-archive-btn"
                        className="border-2 border-black px-5 py-2.5 font-body text-sm uppercase tracking-widest font-semibold hover:bg-black hover:text-white transition-colors"
                      >
                        Archive
                      </button>
                    )}
                    {selectedEnquiry.status === "new" && (
                      <button
                        onClick={() => updateEnquiryStatus(selectedEnquiry.id, "read")}
                        data-testid="enquiry-read-btn"
                        className="border-2 border-black/20 px-5 py-2.5 font-body text-sm uppercase tracking-widest font-semibold text-black/60 hover:text-black hover:border-black transition-colors"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
        {tab === "workshop" && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="bg-white border-2 border-black"
            data-testid="admin-workshop-panel"
          >
            <div className="border-b-2 border-black p-6 flex items-center justify-between gap-4">
              <div>
                <div className="label-caps text-black/50">Momentum Method</div>
                <h3 className="font-display text-2xl">
                  Workshop registrations — {wsSettings ? `${wsSettings.date_short}, ${wsSettings.time_label}` : "…"}
                </h3>
              </div>
              <div className="text-right">
                <div className="font-display text-4xl" data-testid="workshop-count">{registrations.length}</div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-black/50">seats saved</div>
              </div>
            </div>
            <div className="border-b-2 border-black p-6 bg-black/[0.02]" data-testid="workshop-settings">
              <div className="flex flex-wrap items-end gap-4">
                <div className="flex-1 min-w-[220px]">
                  <div className="label-caps text-black/50 mb-1">Workshop settings</div>
                  <div className="font-body text-sm">
                    Round {wsSettings?.round ?? "…"} · currently{" "}
                    <strong data-testid="ws-current-date">{wsSettings ? `${wsSettings.date_label}, ${wsSettings.time_label}` : "…"}</strong>
                  </div>
                </div>
                <div>
                  <label className="label-caps text-black/50 block mb-2">Date</label>
                  <input
                    type="date"
                    value={wsDate}
                    onChange={(e) => setWsDate(e.target.value)}
                    className="border-2 border-black px-3 py-2 font-mono text-sm bg-white"
                    data-testid="ws-date-input"
                  />
                </div>
                <div>
                  <label className="label-caps text-black/50 block mb-2">Start (Brisbane)</label>
                  <input
                    type="time"
                    value={wsTime}
                    onChange={(e) => setWsTime(e.target.value)}
                    className="border-2 border-black px-3 py-2 font-mono text-sm bg-white"
                    data-testid="ws-time-input"
                  />
                </div>
                <button
                  onClick={() => saveWorkshopDate(false)}
                  disabled={wsSaving}
                  className="border-2 border-black px-4 py-2 font-body text-xs uppercase tracking-widest font-semibold hover:bg-black hover:text-white transition-colors disabled:opacity-40"
                  data-testid="ws-save-date-btn"
                >
                  Save date
                </button>
                <button
                  onClick={() => saveWorkshopDate(true)}
                  disabled={wsSaving}
                  className="bg-black text-white px-4 py-2 font-body text-xs uppercase tracking-widest font-semibold hover:bg-[#EC008C] transition-colors disabled:opacity-40"
                  data-testid="ws-new-round-btn"
                >
                  Start next round
                </button>
              </div>
              <p className="font-mono text-[10px] text-black/40 mt-3">
                Save date changes this round's workshop time. Start next round archives the current registrations + waitlist and opens 8 fresh seats on the new date — the program page and emails update automatically.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b-2 border-black text-left">
                    <th className="py-3 px-4 label-caps text-black/60">Name</th>
                    <th className="py-3 px-2 label-caps text-black/60">Business</th>
                    <th className="py-3 px-2 label-caps text-black/60">Position</th>
                    <th className="py-3 px-2 label-caps text-black/60">Team</th>
                    <th className="py-3 px-2 label-caps text-black/60">Contact</th>
                    <th className="py-3 px-4 label-caps text-black/60 text-right">Registered</th>
                  </tr>
                </thead>
                <tbody>
                  {registrations.length === 0 && (
                    <tr><td colSpan={6} className="py-10 text-center text-black/40 font-mono text-sm">No registrations yet</td></tr>
                  )}
                  {registrations.map((r, i) => (
                    <tr key={r.id} className="border-b border-black/10" data-testid={`registration-row-${i}`}>
                      <td className="py-3 px-4 font-body font-semibold">{r.name}</td>
                      <td className="py-3 px-2 font-body">{r.business_name}</td>
                      <td className="py-3 px-2 font-body text-black/60">{r.position}</td>
                      <td className="py-3 px-2 font-mono text-xs whitespace-nowrap">{r.employees}</td>
                      <td className="py-3 px-2">
                        <a href={`mailto:${r.email}`} className="font-mono text-xs hover:text-[#EC008C] transition-colors block">{r.email}</a>
                        <a href={`tel:${r.phone.replace(/\s/g, "")}`} className="font-mono text-xs text-black/50 hover:text-black transition-colors">{r.phone}</a>
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-xs text-black/50 whitespace-nowrap">{formatDateTime(r.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 border-t-2 border-black">
              <div className="p-6 border-b-2 md:border-b-0 md:border-r-2 border-black" data-testid="reminder-panel">
                <div className="label-caps text-black/50 mb-1">Morning of the workshop</div>
                <h4 className="font-display text-xl mb-4">Send a day-of reminder</h4>
                <div className="space-y-4 mb-4">
                  <div>
                    <label className="label-caps text-black/50 block mb-2">Join link (https, optional)</label>
                    <input
                      type="url"
                      value={reminderUrl}
                      onChange={(e) => setReminderUrl(e.target.value)}
                      placeholder="https://zoom.us/j/…"
                      className="w-full border-2 border-black px-3 py-2.5 font-mono text-sm"
                      data-testid="reminder-join-input"
                    />
                  </div>
                  <div>
                    <label className="label-caps text-black/50 block mb-2">Personal note (optional)</label>
                    <input
                      type="text"
                      value={reminderNote}
                      onChange={(e) => setReminderNote(e.target.value)}
                      placeholder="Bring your trickiest admin task!"
                      className="w-full border-2 border-black px-3 py-2.5 font-body text-sm"
                      data-testid="reminder-note-input"
                    />
                  </div>
                </div>
                <button
                  onClick={sendReminder}
                  disabled={reminderSending || registrations.length === 0}
                  className="flex items-center gap-2 bg-black text-white px-5 py-2.5 font-body text-sm uppercase tracking-widest font-semibold hover:bg-[#00AEEF] transition-colors disabled:opacity-40"
                  data-testid="reminder-send-btn"
                >
                  <Mail size={14} />
                  {reminderSending ? "Sending..." : `Email reminder to ${registrations.length} attendee${registrations.length === 1 ? "" : "s"}`}
                </button>
              </div>
              <div className="p-6" data-testid="replay-panel">
              <div className="label-caps text-black/50 mb-1">After the workshop</div>
              <h4 className="font-display text-xl mb-4">Email the replay to all attendees</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="label-caps text-black/50 block mb-2">Recording link (https)</label>
                  <input
                    type="url"
                    value={replayUrl}
                    onChange={(e) => setReplayUrl(e.target.value)}
                    placeholder="https://zoom.us/rec/…"
                    className="w-full border-2 border-black px-3 py-2.5 font-mono text-sm"
                    data-testid="replay-url-input"
                  />
                </div>
                <div>
                  <label className="label-caps text-black/50 block mb-2">Personal note (optional)</label>
                  <input
                    type="text"
                    value={replayNote}
                    onChange={(e) => setReplayNote(e.target.value)}
                    placeholder="Thanks for the great questions!"
                    className="w-full border-2 border-black px-3 py-2.5 font-body text-sm"
                    data-testid="replay-note-input"
                  />
                </div>
              </div>
              <button
                onClick={sendReplay}
                disabled={replaySending || registrations.length === 0}
                className="flex items-center gap-2 bg-black text-white px-5 py-2.5 font-body text-sm uppercase tracking-widest font-semibold hover:bg-[#EC008C] transition-colors disabled:opacity-40"
                data-testid="replay-send-btn"
              >
                <Mail size={14} />
                {replaySending ? "Sending..." : `Email replay to ${registrations.length} attendee${registrations.length === 1 ? "" : "s"}`}
              </button>
              </div>
            </div>
            {waitlist.length > 0 && (
              <div className="border-t-2 border-black p-6" data-testid="waitlist-section">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="label-caps text-black/50">Waitlist</div>
                    <h4 className="font-display text-xl">Waiting for a seat</h4>
                  </div>
                  <div className="font-display text-3xl" data-testid="waitlist-count">{waitlist.length}</div>
                </div>
                <table className="w-full text-sm">
                  <tbody>
                    {waitlist.map((w, i) => (
                      <tr key={w.id} className="border-b border-black/10" data-testid={`waitlist-row-${i}`}>
                        <td className="py-2.5 pr-4 font-body font-semibold">{w.name}</td>
                        <td className="py-2.5 pr-4">
                          <a href={`mailto:${w.email}`} className="font-mono text-xs hover:text-[#EC008C] transition-colors">{w.email}</a>
                        </td>
                        <td className="py-2.5 text-right font-mono text-xs text-black/50 whitespace-nowrap">{formatDateTime(w.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        )}
      </main>

      <Toaster position="bottom-right" richColors />
    </div>
  );
}

function StatCard({ label, value, change, icon, accent, accentDark, testid }) {
  const showChange = typeof change === "number";
  return (
    <div
      className="bg-white border-2 border-black p-5 relative overflow-hidden"
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-6">
        <div
          className="w-10 h-10 flex items-center justify-center"
          style={{ background: accent, color: accentDark ? "#0E0E0E" : "#fff" }}
        >
          {icon}
        </div>
        {showChange && (
          <span className={`flex items-center gap-1 text-xs font-mono ${change >= 0 ? "text-black" : "text-[#EC008C]"}`}>
            {change >= 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
            {Math.abs(change)}%
          </span>
        )}
      </div>
      <div className="label-caps text-black/50 mb-1">{label}</div>
      <div className="font-display text-4xl">{value}</div>
    </div>
  );
}
